import React, { useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/Toast';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import { useCar, useUpdateCar, useDeleteCar } from '@/features/cars/hooks/useCars';
import Badge from '@/components/ui/badge/Badge';
import Button from '@/components/ui/button/Button';
import { Modal } from '@/components/ui/modal';
import { ModalWithSearchParam } from '@/components/ui/modal/ModalWithSearchParam';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { useModal } from '@/shared/hooks/useModal';
import { useModalSearchParam } from '@/shared/hooks/useModalSearchParam';
import CarEditForm from '@/features/cars/components/CarEditForm';
import { useEditCarPrice } from '@/features/cars/api/carCalendarApi';
import { Car, CarWithLoadedModel } from '@/features/cars/api/carsApi';
import { CarStats } from '@/features/cars/components/CarStats';
import { CarPriceCalendar } from '@/features/cars/components/CarPriceCalendar';
import { EditPriceRequest } from '@/features/cars/api/carCalendarApi';
import DetailsCard from '@/components/common/DetailsCard';

const CarDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const { data: car, isLoading, error } = useCar(id!);
  const { isOpen: isDeleteOpen, openModal: openDeleteModal, closeModal: closeDeleteModal } = useModal();
  const { isOpen: isPriceRangeEditOpen, openModal: openPriceRangeEditModal, closeModal: closePriceRangeEditModal } = useModal();
  const updateCar = useUpdateCar();
  const deleteCar = useDeleteCar();
  const editCarPrice = useEditCarPrice();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);

  const { isOpen: isEditOpen, open: openEditModal, close: closeEditModalWithoutCheck } = useModalSearchParam({
    paramName: 'edit'
  });

  const handleModalClose = useCallback(() => {
    formRef.current?.handleClose();
  }, []);

  const handleFormClose = useCallback((hasUnsavedChanges?: boolean) => {
    if (hasUnsavedChanges === false) {
      closeEditModalWithoutCheck();
    }
  }, [closeEditModalWithoutCheck]);

  if (isLoading) {
    return <div>{t('cars.details.errors.loading')}</div>;
  }

  if (error || !car) {
    return <div>{t('cars.details.errors.error_loading')}</div>;
  }

  if (!car.model) {
    return <div>{t('cars.details.errors.model_not_found')}</div>;
  }

  const handleUpdateCar = async (data: Partial<Car>) => {
    try {
      await updateCar.mutateAsync({ id: id!, data });
      closeEditModalWithoutCheck();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteCar = async () => {
    try {
      await deleteCar.mutateAsync(id!);
      toast.success(t('cars.delete.success'));
      window.location.href = '/cars';
    } catch (error) {
      toast.error(t('cars.delete.error') + `<br/><br/>${error}`);
    }
  };

  const handlePriceEdit = async (data: EditPriceRequest) => {
    try {
      await editCarPrice.mutateAsync(data);
      toast.success(t('cars.price_edit.save_success'));
    } catch (error) {
      toast.error(t('cars.price_edit.save_error') + `<br/><br/>${error}`);
    }
  };

  const carWithModel = car as CarWithLoadedModel;

  const drivetrain = {
    'front_2wd': 'Front-Wheel Drive',
    'rear_2wd': 'Rear-Wheel Drive',
    'full_4wd': 'All-Wheel Drive'
  }[carWithModel.drivetrain];

  return (
    <>
      <PageMeta
        title={`${carWithModel.model.brand_name} ${carWithModel.model.model_name} | Admin.Drivitive`}
        description="Car details"
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.cars'), path: '/cars' },
          { label: `${carWithModel.model.brand_name} ${carWithModel.model.model_name} ${car.registration_number}` }
        ]}
        actions={
            <div className="flex gap-3">
              <Button variant="primary" size="md" onClick={openEditModal}>{t('cars.details.edit.button')}</Button>
              <Button variant="danger" size="md" onClick={openDeleteModal}>{t('cars.details.delete.button')}</Button>
            </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3 2xl:gap-7.5">
        {/* Main Car Details */}
        <DetailsCard
          title={t('cars.details.title')}
        >
          <>
            <div className="space-y-4">
              {carWithModel.image_url && (
                <div className="relative">
                  <div className="overflow-hidden rounded-lg">
                    <ImagePreview imageUrl={carWithModel.image_url}>
                      <img
                        src={carWithModel.image_url}
                        alt="Car"
                        className="w-full border border-border-primary rounded-xl"
                      />
                    </ImagePreview>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Link
                  to={`/models/${carWithModel.model.id}`}
                  className="font-medium hover:text-brand-500 transition-colors block"
                  title="View model details"
                >
                  {carWithModel.model.brand_name} {carWithModel.model.model_name}
                </Link>
                <div className="flex gap-2">
                  <Badge size="sm" color="primary">
                    {carWithModel.model.body_type.toUpperCase()}
                  </Badge>
                  <Badge size="sm" color="info">
                    {carWithModel.model.transmission_type.toUpperCase()}
                  </Badge>
                </div>
                <p className="text-sm text-text-tertiary">
                  {t('cars.details.fields.year')}: {car.release_year}
                </p>
                <p className="text-sm text-text-tertiary">
                  {t('cars.details.fields.reg_number')}: {car.registration_number}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-tertiary">{t('cars.details.fields.color')}: </span>
                  <div
                    className="w-6 h-6 rounded-full border border-gray-400"
                    style={{ backgroundColor: car.color }}
                  />
                </div>
                <p className="text-sm text-text-tertiary">
                  {t('cars.details.fields.fuel_type')}: {car.fuel_type}
                </p>
                <p className="text-sm text-text-tertiary">
                  {t('cars.details.fields.fuel_consumption')}: {car.fuel_consumption} l/100km
                </p>
                <p className="text-sm text-text-tertiary">
                  {t('cars.details.fields.engine')}: {car.engine_displacement}L, {car.engine_power}hp
                </p>
              </div>
            </div>
          </>
        </DetailsCard>

        {/* Additional Details */}
        <DetailsCard
          title={t('cars.details.information')}
        >
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.seats')}</p>
              <p className="font-medium text-text-primary">{car.seats_count}</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.doors')}</p>
              <p className="font-medium text-text-primary">{car.doors_count}</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.drivetrain')}</p>
              <p className="font-medium text-text-primary">{drivetrain}</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.clearance')}</p>
              <p className="font-medium text-text-primary">{car.clearance} cm</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.price_per_day')}</p>
              <p className="font-medium text-text-primary">{car.price}</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.mileage_limit')}</p>
              <p className="font-medium text-text-primary">{car.mileage_limit} km/day</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-text-tertiary">{t('cars.details.fields.features')}</p>
              <div className="flex gap-2 mt-1">
                {car.has_gps && (
                  <Badge size="sm" color="success">GPS</Badge>
                )}
                <Badge size="sm" color="primary">
                  Ecology class {car.ecology_class}
                </Badge>
              </div>
            </div>
          </div>
          </>
        </DetailsCard>

        {/* Statistics */}
        <CarStats carId={car.id} />

        {/* Descriptions block */}

        <div className='grid grid-cols-1 gap-8 md:grid-cols-2 col-span-1 md:col-span-3'>
          <DetailsCard
            title={`🇺🇸 ${t('cars.details.english_description')}`}
          >
            <>
              <p className="mt-1 font-normal text-text-primary">
                {car.description_en || 'No description available'}
              </p>
            </>
          </DetailsCard>

          <DetailsCard
            title={`🇫🇷 ${t('cars.details.french_description')}`}
          >
            <>
              <p className="mt-1 font-normal text-text-primary">
                {car.description_fr || 'No description available'}
              </p>
            </>
          </DetailsCard>
        </div>

        {/* Price Calendar */}
        <div className="col-span-1 md:col-span-3">
          <CarPriceCalendar carId={car.id} defaultPrice={parseFloat(car.price)} />
        </div>
      </div>

      {/* Модальное окно редактирования */}
      <ModalWithSearchParam
        paramName="edit"
        maxWidth="1360px"
        className="p-6 lg:p-10"
        onClose={handleModalClose}
      >
        <CarEditForm
          ref={formRef}
          car={car}
          onSubmit={handleUpdateCar}
          onClose={handleFormClose}
          initialData={car}
        />
      </ModalWithSearchParam>

      {/* Модальное окно подтверждения удаления */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        className="max-w-[400px] p-6"
      >
        <div className="text-center">
          <h3 className="mb-4 text-xl font-semibold text-text-primary">
            {t('cars.details.delete.title')}
          </h3>
          <p className="mb-6 text-text-tertiary">
            {t('cars.details.delete.confirmation')}
          </p>
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={closeDeleteModal}
            >
              {t('cars.details.delete.cancel')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDeleteCar}
              disabled={deleteCar.isPending}
            >
              {deleteCar.isPending ? t('cars.details.delete.deleting') : t('cars.details.delete.button')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CarDetailsPage;
